import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import api from "../api";

// Searchable patient picker — select by name, Patient ID (PAT-000001) or phone.
export default function PatientPicker({ value, onSelect, testid }) {
  const [query, setQuery] = useState(value?.name || "");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { setQuery(value?.name || ""); }, [value]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.get("/connect/patients").then((r) => {
        const q = query.trim().toLowerCase();
        const all = r.data || [];
        setOptions(
          (q
            ? all.filter((p) =>
                [p.name, p.patient_id, p.phone].some((v) => String(v || "").toLowerCase().includes(q)))
            : all
          ).slice(0, 8)
        );
      }).catch(() => setOptions([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query, open]);

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          data-testid={testid}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); onSelect(null); }}
          onFocus={() => setOpen(true)}
          placeholder="Search patient — name, PAT ID or phone"
          className="h-11 w-full rounded-xl border border-pine/15 pl-10 pr-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine bg-white"
        />
      </div>
      {value?.patient_id && (
        <p data-testid={`${testid}-selected`} className="mt-1 text-[11px] font-bold text-teal-brand">
          Linked: {value.name} ({value.patient_id})
        </p>
      )}
      {open && options.length > 0 && (
        <div data-testid={`${testid}-options`} className="absolute z-30 mt-1 w-full rounded-xl border border-pine/10 bg-white shadow-grand max-h-56 overflow-y-auto">
          {options.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`${testid}-opt-${(p.patient_id || p.id).toString().replace(/\s/g, "-")}`}
              onClick={() => { onSelect(p); setQuery(p.name); setOpen(false); }}
              className="w-full text-left px-4 py-2.5 hover:bg-cream/60 transition-colors border-b border-pine/5 last:border-0"
            >
              <p className="text-sm font-bold text-pine-deep">{p.name} <span className="text-[10px] font-bold text-gold">{p.patient_id}</span></p>
              <p className="text-[11px] text-slate-500">{p.phone} · {p.treatment || "General"}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
